import { ChevronDownIcon } from '@chakra-ui/icons';
import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import Link from 'next/link';
import { colors } from '../../../constants/constants';
import Logo from '../../../public/images/logo.svg';
import { MainMenu, MainMenuItem } from '../../../types/umbraco';
import MyPages from '../mypages/mypages';
import React, { useEffect, useRef } from 'react';

type Props = {
  menu?: MainMenu;
  scrolled: boolean;
};

const SafeFlex = Flex as React.ComponentType<any>;

type NavigationMenuItemProps = {
  item: MainMenuItem;
};

function NavigationMenuItem({ item }: NavigationMenuItemProps): JSX.Element {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const submenuRef = useRef<HTMLUListElement>(null);
  const shouldFocusFirstItem = useRef(false);
  const menuId = `navigation-submenu-${item.path.replace(/[^a-zA-Z0-9]/g, '-')}`;

  useEffect(() => {
    if (isOpen && shouldFocusFirstItem.current) {
      submenuRef.current?.querySelector<HTMLAnchorElement>('a')?.focus();
      shouldFocusFirstItem.current = false;
    }
  }, [isOpen]);

  function openMenu(focusFirstItem = false): void {
    shouldFocusFirstItem.current = focusFirstItem;
    onOpen();
  }

  function focusSubmenuItem(offset: number): void {
    const items = submenuRef.current?.querySelectorAll<HTMLAnchorElement>('a');

    if (!items?.length) {
      return;
    }

    const currentIndex = Array.from(items).indexOf(
      document.activeElement as HTMLAnchorElement,
    );
    const nextIndex = (currentIndex + offset + items.length) % items.length;
    items[nextIndex].focus();
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      triggerRef.current?.focus();
      return;
    }

    if (event.target === triggerRef.current && event.key === 'ArrowDown') {
      event.preventDefault();
      openMenu(true);
    }
  }

  function handleSubmenuKeyDown(
    event: React.KeyboardEvent<HTMLUListElement>,
  ): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusSubmenuItem(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusSubmenuItem(-1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      submenuRef.current?.querySelector<HTMLAnchorElement>('a')?.focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      const items = submenuRef.current?.querySelectorAll<HTMLAnchorElement>('a');
      items?.[items.length - 1]?.focus();
    }
  }

  return (
    <div
      className="navigation-dropdown"
      onMouseEnter={() => openMenu()}
      onMouseLeave={(event) => {
        if (!event.currentTarget.contains(document.activeElement)) {
          onClose();
        }
      }}
      onFocusCapture={() => openMenu()}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          onClose();
        }
      }}
      onKeyDown={handleKeyDown}>
      <div className="navigation-dropdown__trigger">
        <Link href={item.path} onClick={onClose}>{item.name}</Link>
        <button
          ref={triggerRef}
          type="button"
          aria-label={`Visa undersidor för ${item.name}`}
          aria-haspopup="true"
          aria-expanded={isOpen}
          aria-controls={menuId}
          onFocus={() => openMenu()}
          onClick={() => (isOpen ? onClose() : openMenu())}
          className="navigation-dropdown__button">
          <ChevronDownIcon w={5} h={5} ms={3} />
        </button>
      </div>
      {isOpen && (
        <ul
          id={menuId}
          aria-label={`Undersidor för ${item.name}`}
          className="navigation-dropdown__list"
          style={{ color: colors.neutrals[900] }}
          ref={submenuRef}
          onKeyDown={handleSubmenuKeyDown}>
          <li className="navigation-dropdown__item">
            <Link href="/hyr-forrad" onClick={onClose}>Se alla förråd</Link>
          </li>
          {item.children?.map((y) => {
            return (
              <li className="navigation-dropdown__item" key={y.id ?? y.path}>
                <Link href={`${y.path}`} onClick={onClose}>{y.name}</Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function Navigation({ scrolled, menu }: Props): JSX.Element {
  return (
    <>
      <Box>
        <Link href="/" aria-label="Startsida">
          <Logo fill={colors.custom.primary[900]} height="3em" />
        </Link>
      </Box>

      <Box
        as="nav"
        aria-label="Huvudnavigation"
        w="100%"
        display={{ base: 'none', md: 'flex' }}
        alignItems="center">
        <SafeFlex
          flex="1"
          alignItems="center"
          maxW="md"
          marginLeft="auto"
          paddingLeft="2em"
          paddingRight="2em"
          color={colors.neutrals[900]}>
          <SafeFlex
            alignItems="center"
            justifyContent="space-between"
            flex="1"
            color={colors.neutrals[900]}>
            {menu?.items?.map((x) => {
              return (
                <Box fontSize="16px" fontWeight={600} key={x.path}>
                  {x.children === undefined ? (
                    <Link href={x.path}>{x.name}</Link>
                  ) : (
                    <NavigationMenuItem item={x} />
                  )}
                </Box>
              );
            })}
          </SafeFlex>
        </SafeFlex>

        <Box>
          <MyPages />
        </Box>
      </Box>
    </>
  );
}
